import React, { useState, useEffect, useCallback } from "react";
import { useBluelagoon } from "@bluelagoon";
import { Card, CardContent, CardHeader, CardTitle } from "@shadcn-in-nx/ui/card";
import { Button } from "@shadcn-in-nx/ui/button";
import { Loader2, FileText, ChevronLeft, ChevronRight, User } from "lucide-react";
import { format } from "date-fns";

export default function HourLogList({ subscriptionId }) {
    const [logs, setLogs] = useState([]);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalHoursUsed, setTotalHoursUsed] = useState(0);

    const { call: fetchLogs, isLoading } = useBluelagoon({ method: "getSupportHourLogs" });

    const loadLogs = useCallback(async () => {
        try {
            const res = await fetchLogs({ subscriptionId, page, limit: 20 });
            if (res?.data) {
                setLogs(res.data.logs || []);
                setTotalPages(res.data.totalPages || 1);
                setTotalHoursUsed(res.data.totalHoursUsed || 0);
            }
        } catch (err) {
            console.error("Failed to fetch logs:", err);
        }
    }, [fetchLogs, subscriptionId, page]);

    useEffect(() => { loadLogs(); }, [loadLogs]);

    return (
        <Card>
            <CardHeader>
                <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2 text-lg">
                        <FileText className="h-5 w-5" />Hour Logs
                    </CardTitle>
                    <span className="text-sm text-muted-foreground">Total used: {totalHoursUsed}h</span>
                </div>
            </CardHeader>
            <CardContent>
                {isLoading ? (
                    <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
                ) : logs.length === 0 ? (
                    <p className="text-center text-muted-foreground py-8">No hours logged yet</p>
                ) : (
                    <div className="space-y-3">
                        {logs.map(log => (
                            <div key={log.id} className="border rounded-lg p-3 space-y-1">
                                <div className="flex items-center justify-between">
                                    <span className="font-medium text-sm">{log.description}</span>
                                    <span className="font-semibold text-sm">{log.hoursDeducted}h</span>
                                </div>
                                {log.notes && <p className="text-sm text-muted-foreground">{log.notes}</p>}
                                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                                    <span>{format(new Date(log.createdAt), "MMM d, yyyy h:mm a")}</span>
                                    {log.deductedBy && (
                                        <span className="flex items-center gap-1">
                                            <User className="w-3 h-3" />
                                            {log.deductedBy.firstName} {log.deductedBy.lastName}
                                        </span>
                                    )}
                                    <span>{log.balanceBefore}h &rarr; {log.balanceAfter}h</span>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
                {totalPages > 1 && (
                    <div className="flex items-center justify-center gap-2 mt-4">
                        <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                            <ChevronLeft className="w-4 h-4" />
                        </Button>
                        <span className="text-sm text-muted-foreground">Page {page} of {totalPages}</span>
                        <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
                            <ChevronRight className="w-4 h-4" />
                        </Button>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
